<template>
  <div class="min-h-screen bg-gray-50 p-6 dark:bg-gray-900">
    <div class="mb-6 flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">平台导师结算</h1>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          只统计默认租户下平台自营导师的约课收入。外部租户教师由租户自行结算，不在这里处理。
        </p>
      </div>
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div class="rounded-2xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-sky-700 dark:border-sky-900/60 dark:bg-sky-950/30 dark:text-sky-200">
          每月 8 号后可结算上个自然月，当前最多可结算到 {{ allowedSettlementMonth }}
        </div>
        <button class="page-btn px-4 py-3" type="button" :disabled="pageLoading" @click="refreshAll">
          {{ pageLoading ? '刷新中...' : '刷新数据' }}
        </button>
      </div>
    </div>

    <section class="mb-6 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p class="text-sm font-medium text-gray-500 dark:text-gray-400">结算月份</p>
          <div class="mt-2 flex items-center gap-3">
            <input
              v-model="selectedMonth"
              type="month"
              class="form-input w-[180px]"
            />
            <span class="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              {{ monthBadgeText }}
            </span>
          </div>
        </div>
        <div class="grid grid-cols-2 gap-3 lg:min-w-[700px] lg:grid-cols-5">
          <article class="stat-card">
            <p class="stat-label">待结导师</p>
            <p class="stat-value">{{ overviewStats.tutorCount }}</p>
          </article>
          <article class="stat-card">
            <p class="stat-label">待结订单</p>
            <p class="stat-value">{{ overviewStats.pendingCount }}</p>
          </article>
          <article class="stat-card">
            <p class="stat-label">学生实付</p>
            <p class="stat-value">{{ formatCurrency(overviewStats.grossAmount) }}</p>
          </article>
          <article class="stat-card">
            <p class="stat-label">平台服务费</p>
            <p class="stat-value">{{ formatCurrency(overviewStats.platformAmount) }}</p>
          </article>
          <article class="stat-card">
            <p class="stat-label">导师应结</p>
            <p class="stat-value text-emerald-600 dark:text-emerald-400">{{ formatCurrency(overviewStats.tutorAmount) }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="mb-6">
      <div class="inline-flex rounded-2xl border border-gray-200 bg-white p-1 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="rounded-xl px-4 py-2 text-sm font-medium transition"
          :class="activeTab === tab.id
            ? 'bg-indigo-600 text-white shadow-sm'
            : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'"
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
        </button>
      </div>
    </section>

    <section v-if="activeTab === 'overview'" class="rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div class="flex flex-col gap-4 border-b border-gray-100 px-6 py-5 dark:border-gray-700 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">导师月结</h2>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">按导师汇总当前月份未结算的约课收入。</p>
        </div>
        <div class="w-full lg:w-80">
          <input
            v-model.trim="overviewSearch"
            type="search"
            placeholder="搜索导师姓名或邮箱"
            class="form-input"
          />
        </div>
      </div>

      <div v-if="overviewLoading" class="flex min-h-[280px] items-center justify-center">
        <div class="text-center">
          <div class="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-indigo-100 border-t-indigo-500" />
          <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">正在加载导师月结...</p>
        </div>
      </div>

      <div v-else-if="filteredOverviewItems.length === 0" class="px-6 py-14 text-center">
        <p class="text-base font-medium text-gray-700 dark:text-gray-200">当前月份暂无待结导师</p>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">有平台自营导师约课支付成功后，会在这里按导师汇总。</p>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-900/60">
            <tr>
              <th class="table-th">导师</th>
              <th class="table-th text-right">待结订单</th>
              <th class="table-th text-right">退款调整</th>
              <th class="table-th text-right">学生实付</th>
              <th class="table-th text-right">平台服务费</th>
              <th class="table-th text-right">导师应结</th>
              <th class="table-th text-right">操作</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-for="item in filteredOverviewItems" :key="item.tutorId" class="hover:bg-gray-50 dark:hover:bg-gray-900/40">
              <td class="table-td">
                <div class="font-medium text-gray-900 dark:text-gray-100">{{ item.tutorName || `导师 #${item.tutorId}` }}</div>
                <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ item.tutorEmail || `用户 ID ${item.tutorId}` }}</div>
              </td>
              <td class="table-td text-right">{{ item.pendingCount }}</td>
              <td class="table-td text-right">{{ item.refundAdjustmentCount }}</td>
              <td class="table-td text-right">{{ formatCurrency(item.pendingGrossAmount) }}</td>
              <td class="table-td text-right">{{ formatCurrency(item.pendingPlatformAmount) }}</td>
              <td class="table-td text-right font-semibold text-emerald-600 dark:text-emerald-400">{{ formatCurrency(item.pendingTutorAmount) }}</td>
              <td class="table-td text-right">
                <button class="primary-btn" type="button" :disabled="previewLoading" @click="openPreview(item)">
                  预览结算
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-else-if="activeTab === 'batches'" class="rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div class="flex flex-col gap-4 border-b border-gray-100 px-6 py-5 dark:border-gray-700 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">结算批次</h2>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">回查已经确认打款的导师结算批次。</p>
        </div>
      </div>

      <div v-if="batchesLoading" class="flex min-h-[220px] items-center justify-center">
        <div class="text-center">
          <div class="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-indigo-100 border-t-indigo-500" />
          <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">正在加载批次...</p>
        </div>
      </div>

      <div v-else-if="batchItems.length === 0" class="px-6 py-14 text-center">
        <p class="text-base font-medium text-gray-700 dark:text-gray-200">当前月份暂无结算批次</p>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">在“导师月结”确认打款后，会生成批次记录。</p>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-900/60">
            <tr>
              <th class="table-th">批次</th>
              <th class="table-th">导师</th>
              <th class="table-th">月份</th>
              <th class="table-th text-right">记录数</th>
              <th class="table-th text-right">学生实付</th>
              <th class="table-th text-right">平台服务费</th>
              <th class="table-th text-right">已结金额</th>
              <th class="table-th">打款时间</th>
              <th class="table-th text-right">操作</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-for="batch in batchItems" :key="batch.id" class="hover:bg-gray-50 dark:hover:bg-gray-900/40">
              <td class="table-td font-medium text-gray-900 dark:text-gray-100">#{{ batch.id }}</td>
              <td class="table-td">{{ batch.tutorName || `导师 #${batch.tutorId}` }}</td>
              <td class="table-td">{{ formatMonth(batch.settlementMonth) }}</td>
              <td class="table-td text-right">{{ batch.recordCount }}</td>
              <td class="table-td text-right">{{ formatCurrency(batch.grossAmount) }}</td>
              <td class="table-td text-right">{{ formatCurrency(batch.platformAmount) }}</td>
              <td class="table-td text-right font-semibold text-emerald-600 dark:text-emerald-400">{{ formatCurrency(batch.tutorAmount) }}</td>
              <td class="table-td">{{ formatDateTime(batch.confirmedAt) }}</td>
              <td class="table-td text-right">
                <button class="secondary-btn" type="button" @click="openBatchDetail(batch.id)">详情</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="batchPagination.total > 0" class="border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-900/60">
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-700 dark:text-gray-300">共 {{ batchPagination.total }} 个批次</div>
          <div class="flex gap-2">
            <button class="page-btn" type="button" :disabled="batchPagination.page === 1" @click="prevBatchPage">上一页</button>
            <button class="page-btn" type="button" :disabled="batchPagination.page * batchPagination.limit >= batchPagination.total" @click="nextBatchPage">下一页</button>
          </div>
        </div>
      </div>
    </section>

    <section v-else class="rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div class="flex flex-col gap-4 border-b border-gray-100 px-6 py-5 dark:border-gray-700 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">订单明细</h2>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">审计默认租户下平台导师约课结算账本。</p>
        </div>
        <select v-model="recordFilters.status" class="form-input w-full lg:w-48" @change="handleRecordFilterChange">
          <option :value="null">全部状态</option>
          <option value="PENDING">待结算</option>
          <option value="SETTLED">已结算</option>
          <option value="VOIDED">已作废</option>
        </select>
      </div>

      <div v-if="recordsLoading" class="flex min-h-[220px] items-center justify-center">
        <div class="text-center">
          <div class="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-indigo-100 border-t-indigo-500" />
          <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">正在加载订单明细...</p>
        </div>
      </div>

      <div v-else-if="recordItems.length === 0" class="px-6 py-14 text-center">
        <p class="text-base font-medium text-gray-700 dark:text-gray-200">当前筛选条件下暂无订单明细</p>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-900/60">
            <tr>
              <th class="table-th">订单号</th>
              <th class="table-th">导师</th>
              <th class="table-th">学生</th>
              <th class="table-th">类型</th>
              <th class="table-th text-right">学生实付</th>
              <th class="table-th text-right">平台服务费</th>
              <th class="table-th text-right">导师应结</th>
              <th class="table-th">状态</th>
              <th class="table-th">生效时间</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-for="record in recordItems" :key="record.id" class="hover:bg-gray-50 dark:hover:bg-gray-900/40">
              <td class="table-td font-medium text-gray-900 dark:text-gray-100">{{ record.sourceOrderNo }}</td>
              <td class="table-td">{{ record.tutorName || `导师 #${record.tutorId}` }}</td>
              <td class="table-td">{{ record.userName || record.userEmail || `用户 #${record.userId}` }}</td>
              <td class="table-td">{{ recordKindLabel(record.recordKind) }}</td>
              <td class="table-td text-right">{{ formatCurrency(record.grossAmount) }}</td>
              <td class="table-td text-right">{{ formatCurrency(record.platformAmount) }}</td>
              <td class="table-td text-right font-semibold text-emerald-600 dark:text-emerald-400">{{ formatCurrency(record.tutorAmount) }}</td>
              <td class="table-td">
                <span class="status-badge" :class="statusClass(record.status)">{{ statusLabel(record.status) }}</span>
              </td>
              <td class="table-td">{{ formatDateTime(record.paidAt) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="recordPagination.total > 0" class="border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-900/60">
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-700 dark:text-gray-300">共 {{ recordPagination.total }} 条记录</div>
          <div class="flex gap-2">
            <button class="page-btn" type="button" :disabled="recordPagination.page === 1" @click="prevRecordPage">上一页</button>
            <button class="page-btn" type="button" :disabled="recordPagination.page * recordPagination.limit >= recordPagination.total" @click="nextRecordPage">下一页</button>
          </div>
        </div>
      </div>
    </section>

    <div v-if="previewOpen" class="fixed inset-0 z-50 flex justify-end bg-black/40">
      <div class="h-full w-full max-w-5xl overflow-y-auto bg-white shadow-2xl dark:bg-gray-900">
        <div class="sticky top-0 z-10 flex items-start justify-between border-b border-gray-200 bg-white px-6 py-5 dark:border-gray-700 dark:bg-gray-900">
          <div>
            <p class="text-sm font-medium text-indigo-600 dark:text-indigo-400">结算预览</p>
            <h3 class="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">{{ previewTutorName }} 的 {{ selectedMonth }} 结算</h3>
          </div>
          <button class="secondary-btn" type="button" :disabled="confirmSubmitting" @click="closePreview">关闭</button>
        </div>

        <div v-if="previewLoading" class="flex min-h-[420px] items-center justify-center">
          <div class="text-center">
            <div class="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-indigo-100 border-t-indigo-500" />
            <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">正在加载结算预览...</p>
          </div>
        </div>

        <div v-else-if="previewData" class="space-y-6 p-6">
          <section class="grid grid-cols-1 gap-4 rounded-2xl bg-gray-50 p-4 dark:bg-gray-800 md:grid-cols-4">
            <div>
              <p class="stat-label">记录数</p>
              <p class="mt-1 text-lg font-bold text-gray-900 dark:text-gray-100">{{ previewData.recordCount }}</p>
            </div>
            <div>
              <p class="stat-label">学生实付</p>
              <p class="mt-1 text-lg font-bold text-gray-900 dark:text-gray-100">{{ formatCurrency(previewData.grossAmount) }}</p>
            </div>
            <div>
              <p class="stat-label">平台服务费</p>
              <p class="mt-1 text-lg font-bold text-gray-900 dark:text-gray-100">{{ formatCurrency(previewData.platformAmount) }}</p>
            </div>
            <div>
              <p class="stat-label">导师应结</p>
              <p class="mt-1 text-lg font-bold text-emerald-600 dark:text-emerald-400">{{ formatCurrency(previewData.tutorAmount) }}</p>
            </div>
          </section>

          <section
            class="rounded-2xl px-4 py-3 text-sm"
            :class="previewData.canSettle
              ? 'border border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200'
              : 'border border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-900/60 dark:bg-sky-950/30 dark:text-sky-200'"
          >
            {{ previewData.settlementHint || '仅包含默认租户下该导师当月未结算的约课账本记录。确认后将生成导师结算批次并锁定本次明细。' }}
          </section>

          <section class="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <label>
              <span class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">打款流水号</span>
              <input
                v-model.trim="confirmForm.paymentReference"
                type="text"
                placeholder="例如 BANK-20260410-001"
                class="form-input"
                :class="confirmError ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-100' : ''"
                :disabled="!previewData.canSettle"
                @input="confirmError = ''"
              />
              <p v-if="confirmError" class="mt-1 text-xs font-medium text-rose-600">{{ confirmError }}</p>
            </label>
            <label>
              <span class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">备注</span>
              <input
                v-model.trim="confirmForm.note"
                type="text"
                placeholder="例如 5 月导师结算，线下银行转账"
                class="form-input"
                :disabled="!previewData.canSettle"
              />
            </label>
          </section>

          <section class="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700">
            <div class="border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
              <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100">结算订单</h4>
            </div>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-white dark:bg-gray-900">
                  <tr>
                    <th class="table-th">订单号</th>
                    <th class="table-th">学生</th>
                    <th class="table-th">类型</th>
                    <th class="table-th text-right">学生实付</th>
                    <th class="table-th text-right">平台服务费</th>
                    <th class="table-th text-right">导师应结</th>
                    <th class="table-th">生效时间</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                  <tr v-for="record in previewData.records" :key="record.id" class="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td class="table-td font-medium text-gray-900 dark:text-gray-100">{{ record.sourceOrderNo }}</td>
                    <td class="table-td">{{ record.userName || record.userEmail || `用户 #${record.userId}` }}</td>
                    <td class="table-td">{{ recordKindLabel(record.recordKind) }}</td>
                    <td class="table-td text-right">{{ formatCurrency(record.grossAmount) }}</td>
                    <td class="table-td text-right">{{ formatCurrency(record.platformAmount) }}</td>
                    <td class="table-td text-right font-semibold text-emerald-600 dark:text-emerald-400">{{ formatCurrency(record.tutorAmount) }}</td>
                    <td class="table-td">{{ formatDateTime(record.paidAt) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <div class="sticky bottom-0 flex justify-end gap-3 border-t border-gray-200 bg-white px-6 py-4 dark:border-gray-700 dark:bg-gray-900">
          <button class="secondary-btn" type="button" :disabled="confirmSubmitting" @click="closePreview">取消</button>
          <button
            class="primary-btn"
            type="button"
            :disabled="!previewData || !previewData.canSettle || previewData.recordCount === 0 || confirmSubmitting || previewLoading"
            @click="confirmSettlement"
          >
            {{ confirmSubmitting ? '确认中...' : previewData?.canSettle ? `确认已打款 ${formatCurrency(previewData.tutorAmount)}` : `当前最多可结算到 ${previewData?.allowedSettlementMonth || allowedSettlementMonth}` }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="detailOpen" class="fixed inset-0 z-50 flex justify-end bg-black/40">
      <div class="h-full w-full max-w-5xl overflow-y-auto bg-white shadow-2xl dark:bg-gray-900">
        <div class="sticky top-0 z-10 flex items-start justify-between border-b border-gray-200 bg-white px-6 py-5 dark:border-gray-700 dark:bg-gray-900">
          <div>
            <p class="text-sm font-medium text-indigo-600 dark:text-indigo-400">结算批次详情</p>
            <h3 class="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
              {{ detailData?.batch.tutorName || '导师' }} · 批次 #{{ detailData?.batch.id || '-' }}
            </h3>
          </div>
          <button class="secondary-btn" type="button" @click="closeDetail">关闭</button>
        </div>

        <div v-if="detailLoading" class="flex min-h-[420px] items-center justify-center">
          <div class="text-center">
            <div class="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-indigo-100 border-t-indigo-500" />
            <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">正在加载批次详情...</p>
          </div>
        </div>

        <div v-else-if="detailData" class="space-y-6 p-6">
          <section class="grid grid-cols-1 gap-4 rounded-2xl bg-gray-50 p-4 dark:bg-gray-800 md:grid-cols-4">
            <div>
              <p class="stat-label">结算月份</p>
              <p class="mt-1 text-lg font-bold text-gray-900 dark:text-gray-100">{{ formatMonth(detailData.batch.settlementMonth) }}</p>
            </div>
            <div>
              <p class="stat-label">记录数</p>
              <p class="mt-1 text-lg font-bold text-gray-900 dark:text-gray-100">{{ detailData.batch.recordCount }}</p>
            </div>
            <div>
              <p class="stat-label">打款流水号</p>
              <p class="mt-1 text-lg font-bold text-gray-900 dark:text-gray-100">{{ detailData.batch.paymentReference || '-' }}</p>
            </div>
            <div>
              <p class="stat-label">已结金额</p>
              <p class="mt-1 text-lg font-bold text-emerald-600 dark:text-emerald-400">{{ formatCurrency(detailData.batch.tutorAmount) }}</p>
            </div>
          </section>

          <section class="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700">
            <div class="border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
              <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100">批次订单</h4>
            </div>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-white dark:bg-gray-900">
                  <tr>
                    <th class="table-th">订单号</th>
                    <th class="table-th">学生</th>
                    <th class="table-th">类型</th>
                    <th class="table-th text-right">学生实付</th>
                    <th class="table-th text-right">平台服务费</th>
                    <th class="table-th text-right">导师应结</th>
                    <th class="table-th">生效时间</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                  <tr v-for="record in detailData.records" :key="record.id">
                    <td class="table-td font-medium text-gray-900 dark:text-gray-100">{{ record.sourceOrderNo }}</td>
                    <td class="table-td">{{ record.userName || record.userEmail || `用户 #${record.userId}` }}</td>
                    <td class="table-td">{{ recordKindLabel(record.recordKind) }}</td>
                    <td class="table-td text-right">{{ formatCurrency(record.grossAmount) }}</td>
                    <td class="table-td text-right">{{ formatCurrency(record.platformAmount) }}</td>
                    <td class="table-td text-right font-semibold text-emerald-600 dark:text-emerald-400">{{ formatCurrency(record.tutorAmount) }}</td>
                    <td class="table-td">{{ formatDateTime(record.paidAt) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'

import {
  confirmPlatformTutorSettlementAPI,
  getPlatformTutorSettlementBatchDetailAPI,
  getPlatformTutorSettlementBatchesAPI,
  getPlatformTutorSettlementOverviewAPI,
  getPlatformTutorSettlementRecordsAPI,
  previewPlatformTutorSettlementAPI,
} from '@/api/admin/platformTutorSettlement'
import type {
  PlatformTutorMonthlyOverviewItem,
  PlatformTutorSettlementBatch,
  PlatformTutorSettlementBatchDetailResponse,
  PlatformTutorSettlementPreviewResponse,
  PlatformTutorSettlementRecord,
  PlatformTutorSettlementStatus,
} from '@/types/admin/platformTutorSettlement'
import { formatCurrency, formatDateTime } from '@/utils/formatters'
import toaster from '@/utils/toaster'

type SettlementTab = 'overview' | 'batches' | 'records'

const tabs: Array<{ id: SettlementTab; label: string }> = [
  { id: 'overview', label: '导师月结' },
  { id: 'batches', label: '结算批次' },
  { id: 'records', label: '订单明细' },
]

const activeTab = ref<SettlementTab>('overview')
const selectedMonth = ref(getPreviousMonth())
const pageLoading = ref(false)
const overviewLoading = ref(false)
const batchesLoading = ref(false)
const recordsLoading = ref(false)
const previewLoading = ref(false)
const confirmSubmitting = ref(false)
const detailLoading = ref(false)

const overviewItems = ref<PlatformTutorMonthlyOverviewItem[]>([])
const batchItems = ref<PlatformTutorSettlementBatch[]>([])
const recordItems = ref<PlatformTutorSettlementRecord[]>([])
const previewData = ref<PlatformTutorSettlementPreviewResponse | null>(null)
const detailData = ref<PlatformTutorSettlementBatchDetailResponse | null>(null)
const previewOpen = ref(false)
const detailOpen = ref(false)
const selectedTutor = ref<PlatformTutorMonthlyOverviewItem | null>(null)
const overviewSearch = ref('')
const confirmError = ref('')

const batchPagination = reactive({
  page: 1,
  limit: 20,
  total: 0,
})

const recordPagination = reactive({
  page: 1,
  limit: 30,
  total: 0,
})

const recordFilters = reactive<{
  status: PlatformTutorSettlementStatus | null
}>({
  status: null,
})

const confirmForm = reactive({
  paymentReference: '',
  note: '',
})

function getPreviousMonth(): string {
  const current = new Date()
  current.setMonth(current.getMonth() - 1)
  const year = current.getFullYear()
  const month = `${current.getMonth() + 1}`.padStart(2, '0')
  return `${year}-${month}`
}

function getAllowedSettlementMonth(): string {
  const current = new Date()
  const currentDay = current.getDate()
  const offset = currentDay >= 8 ? 1 : 2
  current.setMonth(current.getMonth() - offset)
  const year = current.getFullYear()
  const month = `${current.getMonth() + 1}`.padStart(2, '0')
  return `${year}-${month}`
}

const allowedSettlementMonth = computed(() => getAllowedSettlementMonth())
const selectedMonthIsSettleable = computed(() => selectedMonth.value <= allowedSettlementMonth.value)

const monthBadgeText = computed(() => {
  return selectedMonthIsSettleable.value
    ? `当前查看 ${selectedMonth.value} 平台导师结算`
    : `${selectedMonth.value} 仅支持查看明细，不可确认结算`
})

const overviewStats = computed(() => {
  return overviewItems.value.reduce(
    (acc, item) => {
      acc.tutorCount += 1
      acc.pendingCount += item.pendingCount
      acc.grossAmount += item.pendingGrossAmount
      acc.platformAmount += item.pendingPlatformAmount
      acc.tutorAmount += item.pendingTutorAmount
      return acc
    },
    { tutorCount: 0, pendingCount: 0, grossAmount: 0, platformAmount: 0, tutorAmount: 0 },
  )
})

const filteredOverviewItems = computed(() => {
  const keyword = overviewSearch.value.trim().toLowerCase()
  if (!keyword) return overviewItems.value
  return overviewItems.value.filter((item) => {
    return item.tutorName.toLowerCase().includes(keyword)
      || (item.tutorEmail || '').toLowerCase().includes(keyword)
      || `${item.tutorId}`.includes(keyword)
  })
})

const previewTutorName = computed(() => {
  return selectedTutor.value?.tutorName || (selectedTutor.value ? `导师 #${selectedTutor.value.tutorId}` : '导师')
})

async function loadOverview() {
  overviewLoading.value = true
  try {
    const response = await getPlatformTutorSettlementOverviewAPI(selectedMonth.value)
    if (response.status === 200 && response.data) {
      overviewItems.value = response.data.tutors
      return
    }
    overviewItems.value = []
    toaster.error(response.message || '加载平台导师月结失败')
  } catch (error) {
    console.error('加载平台导师月结失败:', error)
    overviewItems.value = []
    toaster.error('加载平台导师月结失败')
  } finally {
    overviewLoading.value = false
  }
}

async function loadBatches() {
  batchesLoading.value = true
  try {
    const response = await getPlatformTutorSettlementBatchesAPI({
      settlementMonth: selectedMonth.value,
      limit: batchPagination.limit,
      offset: (batchPagination.page - 1) * batchPagination.limit,
    })
    if (response.status === 200 && response.data) {
      batchItems.value = response.data.batches
      batchPagination.total = response.data.total
      return
    }
    batchItems.value = []
    batchPagination.total = 0
    toaster.error(response.message || '加载平台导师结算批次失败')
  } catch (error) {
    console.error('加载平台导师结算批次失败:', error)
    batchItems.value = []
    batchPagination.total = 0
    toaster.error('加载平台导师结算批次失败')
  } finally {
    batchesLoading.value = false
  }
}

async function loadRecords() {
  recordsLoading.value = true
  try {
    const response = await getPlatformTutorSettlementRecordsAPI({
      settlementMonth: selectedMonth.value,
      status: recordFilters.status,
      page: recordPagination.page,
      limit: recordPagination.limit,
    })
    if (response.status === 200 && response.data) {
      recordItems.value = response.data.records
      recordPagination.total = response.data.total
      return
    }
    recordItems.value = []
    recordPagination.total = 0
    toaster.error(response.message || '加载平台导师订单明细失败')
  } catch (error) {
    console.error('加载平台导师订单明细失败:', error)
    recordItems.value = []
    recordPagination.total = 0
    toaster.error('加载平台导师订单明细失败')
  } finally {
    recordsLoading.value = false
  }
}

async function openPreview(item: PlatformTutorMonthlyOverviewItem) {
  selectedTutor.value = item
  previewOpen.value = true
  previewLoading.value = true
  previewData.value = null
  confirmForm.paymentReference = ''
  confirmForm.note = ''
  confirmError.value = ''
  try {
    const response = await previewPlatformTutorSettlementAPI({
      tutorId: item.tutorId,
      settlementMonth: selectedMonth.value,
    })
    if (response.status === 200 && response.data) {
      previewData.value = response.data
      return
    }
    toaster.error(response.message || '加载结算预览失败')
    closePreview()
  } catch (error) {
    console.error('加载结算预览失败:', error)
    toaster.error('加载结算预览失败')
    closePreview()
  } finally {
    previewLoading.value = false
  }
}

function closePreview() {
  previewOpen.value = false
  previewLoading.value = false
  previewData.value = null
  selectedTutor.value = null
  confirmError.value = ''
}

async function confirmSettlement() {
  if (!previewData.value || !selectedTutor.value) return
  confirmError.value = ''
  if (!confirmForm.paymentReference.trim()) {
    confirmError.value = '请填写实际打款流水号'
    return
  }

  confirmSubmitting.value = true
  try {
    const response = await confirmPlatformTutorSettlementAPI({
      tutorId: selectedTutor.value.tutorId,
      settlementMonth: selectedMonth.value,
      paymentReference: confirmForm.paymentReference.trim(),
      note: confirmForm.note.trim() || undefined,
    })
    if (response.status === 200 && response.data) {
      toaster.success(`导师结算已确认，批次 #${response.data.batch.id}`)
      closePreview()
      await Promise.all([loadOverview(), loadBatches(), loadRecords()])
      return
    }
    toaster.error(response.message || '确认导师结算失败')
  } catch (error) {
    console.error('确认导师结算失败:', error)
    toaster.error('确认导师结算失败')
  } finally {
    confirmSubmitting.value = false
  }
}

async function openBatchDetail(batchId: number) {
  detailOpen.value = true
  detailLoading.value = true
  detailData.value = null
  try {
    const response = await getPlatformTutorSettlementBatchDetailAPI(batchId)
    if (response.status === 200 && response.data) {
      detailData.value = response.data
      return
    }
    toaster.error(response.message || '加载批次详情失败')
    closeDetail()
  } catch (error) {
    console.error('加载批次详情失败:', error)
    toaster.error('加载批次详情失败')
    closeDetail()
  } finally {
    detailLoading.value = false
  }
}

function closeDetail() {
  detailOpen.value = false
  detailLoading.value = false
  detailData.value = null
}

function handleRecordFilterChange() {
  recordPagination.page = 1
  void loadRecords()
}

function prevBatchPage() {
  if (batchPagination.page <= 1) return
  batchPagination.page -= 1
  void loadBatches()
}

function nextBatchPage() {
  if (batchPagination.page * batchPagination.limit >= batchPagination.total) return
  batchPagination.page += 1
  void loadBatches()
}

function prevRecordPage() {
  if (recordPagination.page <= 1) return
  recordPagination.page -= 1
  void loadRecords()
}

function nextRecordPage() {
  if (recordPagination.page * recordPagination.limit >= recordPagination.total) return
  recordPagination.page += 1
  void loadRecords()
}

async function refreshAll() {
  pageLoading.value = true
  try {
    await Promise.all([loadOverview(), loadBatches(), loadRecords()])
  } finally {
    pageLoading.value = false
  }
}

function formatMonth(value: string): string {
  return value.slice(0, 7)
}

function recordKindLabel(kind: string): string {
  return kind === 'REFUND_ADJUSTMENT' ? '退款调整' : '约课收入'
}

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    PENDING: '待结算',
    SETTLED: '已结算',
    VOIDED: '已作废',
  }
  return labels[status] || status
}

function statusClass(status: string): string {
  const classes: Record<string, string> = {
    PENDING: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300',
    SETTLED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300',
    VOIDED: 'bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300',
  }
  return classes[status] || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
}

watch(selectedMonth, async () => {
  batchPagination.page = 1
  recordPagination.page = 1
  await Promise.all([loadOverview(), loadBatches(), loadRecords()])
})

watch(activeTab, (tab) => {
  if (tab === 'batches' && batchItems.value.length === 0) {
    void loadBatches()
  }
  if (tab === 'records' && recordItems.value.length === 0) {
    void loadRecords()
  }
})

onMounted(() => {
  void refreshAll()
})
</script>

<style scoped>
.form-input {
  @apply rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:focus:ring-indigo-900;
}

.stat-card {
  @apply rounded-2xl bg-gray-50 px-4 py-4 dark:bg-gray-900/60;
}

.stat-label {
  @apply text-xs font-medium uppercase tracking-wide text-gray-400;
}

.stat-value {
  @apply mt-2 text-lg font-semibold text-gray-900 dark:text-gray-100;
}

.table-th {
  @apply px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400;
}

.table-td {
  @apply px-4 py-4 text-sm text-gray-600 dark:text-gray-300;
}

.page-btn {
  @apply rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700;
}

.primary-btn {
  @apply rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60;
}

.secondary-btn {
  @apply rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700;
}

.status-badge {
  @apply inline-flex rounded-full px-2.5 py-1 text-xs font-semibold;
}
</style>
